"use client";

import { createContext, useCallback, useState } from "react";

import { useTranslations } from "next-intl";

import { useNonNullableContext } from "@/src/components/hooks";
import { usePathname } from "@/src/intl/routing";
import { WithChildren } from "@/src/types/generic-react-props";
import { useGetErrorMessage } from "@/src/utils/error/use-error-message";

export interface ErrorObject {
  message: string;
}

const isErrorObject = (error: unknown): error is ErrorObject =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  typeof error.message === "string";

interface ErrorMessageContextProps {
  errorMessages: string[];
  putErrors: (...errors: unknown[]) => void;
  removeError: () => void;
}

export type PutErrorsFunc = ErrorMessageContextProps["putErrors"];
export type PutErrorsParams = Parameters<ErrorMessageContextProps["putErrors"]>;

const ErrorMessageContext = createContext<ErrorMessageContextProps | undefined>(
  undefined,
);

export const useErrorMessageContext = () =>
  useNonNullableContext(ErrorMessageContext, "useErrorMessageContext");

export const ErrorMessageProvider = ({ children }: WithChildren) => {
  const t = useTranslations("common");
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [prevPathname, setPrevPathname] = useState<string | null>(null);
  const getErrorMessage = useGetErrorMessage();
  const pathname = usePathname();

  if (prevPathname !== null && prevPathname !== pathname) {
    setErrorMessages([]);
  }

  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
  }

  // useCallback to make function safe to pass into dependencies of useEffect or other hooks
  const putErrors = useCallback(
    (...errors: PutErrorsParams) => {
      if (errors.length === 0) {
        errors = [t("unknown-error")];
      }

      setErrorMessages((prevErrors) =>
        prevErrors.concat(
          errors.map((error) => {
            const message =
              typeof error === "string"
                ? error
                : isErrorObject(error) && error.message !== ""
                  ? getErrorMessage(error).message
                  : "";

            return message.length > 0 ? message : t("unknown-error");
          }),
        ),
      );
    },
    [getErrorMessage, t],
  );

  const removeError = () => {
    setErrorMessages([]);
  };

  const value: ErrorMessageContextProps = {
    // Remove potential duplicates
    errorMessages: [...new Set(errorMessages)],
    putErrors,
    removeError,
  };

  return <ErrorMessageContext value={value}>{children}</ErrorMessageContext>;
};
